import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCY_CONFIG, EXCHANGE_RATES, getCurrencyForLanguage } from '../utils/currency';

const CurrencyContext = createContext();

export const useCurrency = () => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return context;
};

export const CurrencyProvider = ({ children }) => {
    const { i18n } = useTranslation();
    const [currency, setCurrency] = useState(() => {
        // Try to get saved currency from localStorage
        const saved = localStorage.getItem('selectedCurrency');
        if (saved && CURRENCY_CONFIG[saved]) return saved;
        // Default based on language
        return getCurrencyForLanguage(i18n.language);
    });

    // When language changes, update currency to match (unless user manually set one)
    useEffect(() => {
        const manuallySet = localStorage.getItem('currencyManuallySet');
        if (!manuallySet) {
            setCurrency(getCurrencyForLanguage(i18n.language));
        }
    }, [i18n.language]);

    const changeCurrency = (newCurrency) => {
        if (CURRENCY_CONFIG[newCurrency]) {
            setCurrency(newCurrency);
            localStorage.setItem('selectedCurrency', newCurrency);
            localStorage.setItem('currencyManuallySet', 'true');
        }
    };

    // Reset to language-based currency
    const resetCurrency = () => {
        localStorage.removeItem('selectedCurrency');
        localStorage.removeItem('currencyManuallySet');
        setCurrency(getCurrencyForLanguage(i18n.language));
    };

    const availableCurrencies = Object.keys(CURRENCY_CONFIG);

    const value = {
        currency,
        changeCurrency,
        resetCurrency,
        availableCurrencies,
        currencyConfig: CURRENCY_CONFIG[currency],
        exchangeRate: EXCHANGE_RATES[currency],
    };

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
};

export default CurrencyContext;
